import { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import agentsService from '@/services/agents.service';
import assistantService from '@/services/assistant.service';
import { AgentListItem } from '@/types/agents';
import { AssistantSummary } from '@/types/assistant';
import { useToast } from '@/contexts/ToastContext';
import LoadingSpinner from '@/components/LoadingSpinner';
import AgentCard from '@/components/AgentCard';
import AssistantCard from '@/components/AssistantCard';
import TermsModal from '@/components/TermsModal';
import QuotaIndicator from '@/components/QuotaIndicator';

type Tab = 'mine' | 'available';

const AgentsPage: FC = () => {
  const [tab, setTab] = useState<Tab>('mine');
  const [mine, setMine] = useState<AgentListItem[]>([]);
  const [available, setAvailable] = useState<AgentListItem[]>([]);
  // J07: assistentes (classe GENERAL — H.Bot, H.Lex…) acessíveis ao user.
  const [assistants, setAssistants] = useState<AssistantSummary[]>([]);
  const [pinnedIds, setPinnedIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { showToast } = useToast();

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      // Todas as fontes são best-effort e independentes: o Hub é gated por feature/setor
      // (P04) e os assistentes têm a própria flag — uma falhar NÃO derruba a página, pra
      // quem só tem acesso a uma das partes (ex.: Jurídico vê o H.Lex sem ter o Hub).
      const [m, a, asst, pinned] = await Promise.all([
        agentsService.listMyAgents().catch(() => []),
        agentsService.listAvailable().catch(() => []),
        assistantService.listAssistants().catch(() => []),
        agentsService.listPinned().catch(() => []),
      ]);
      if (cancelled) return;
      setMine(m);
      setAvailable(a);
      setAssistants(asst);
      setPinnedIds(new Set(pinned.map((p) => p.agent_id)));
      setLoading(false);
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const openAssistant = (a: AssistantSummary) => navigate(a.is_default ? '/assistente' : `/assistente/${a.id}`);

  const handleTogglePin = async (agent: AgentListItem) => {
    const isPinned = pinnedIds.has(agent.id);
    // Otimista — reverte no erro.
    setPinnedIds((prev) => {
      const next = new Set(prev);
      if (isPinned) next.delete(agent.id);
      else next.add(agent.id);
      return next;
    });
    try {
      if (isPinned) await agentsService.unpin(agent.id);
      else await agentsService.pin(agent.id);
      showToast(isPinned ? 'Agente desafixado.' : 'Agente fixado no dashboard.', 'success');
    } catch {
      setPinnedIds((prev) => {
        const next = new Set(prev);
        if (isPinned) next.add(agent.id);
        else next.delete(agent.id);
        return next;
      });
      showToast('Não foi possível atualizar os favoritos.', 'error');
    }
  };

  const handleDelete = async (agent: AgentListItem) => {
    if (!window.confirm(`Excluir o agente "${agent.name}"? Os arquivos e conversas serão removidos.`)) return;
    try {
      await agentsService.deleteAgent(agent.id);
      setMine((prev) => prev.filter((a) => a.id !== agent.id));
      showToast('Agente excluído.', 'success');
    } catch {
      showToast('Não foi possível excluir o agente.', 'error');
    }
  };

  const list = tab === 'mine' ? mine : available;

  return (
    <div className="p-6 md:p-10 max-w-6xl mx-auto">
      {/* P13: termo de uso G5 — aparece no 1º acesso */}
      <TermsModal />
      <header className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <span className="material-symbols-outlined text-accent text-4xl">psychology</span>
          <h1 className="text-3xl font-bold text-content-primary">Hub de Agentes</h1>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <QuotaIndicator />
          <button
            type="button"
            data-testid="new-agent"
            onClick={() => navigate('/agentes/novo')}
            className="inline-flex items-center gap-1 bg-accent text-surface-primary font-semibold px-4 py-2 rounded-lg hover:opacity-90 transition"
          >
            <span className="material-symbols-outlined text-base">add</span>
            Novo agente
          </button>
        </div>
      </header>

      {loading && (
        <div className="flex justify-center py-16">
          <LoadingSpinner size="large" />
        </div>
      )}

      {/* J07: Assistentes (classe GENERAL — H.Bot, H.Lex…). Abrem a UI do assistente, não a do Hub. */}
      {!loading && assistants.length > 0 && (
        <section className="mb-8" data-testid="assistants-section">
          <h2 className="text-lg font-bold text-content-primary mb-3 flex items-center gap-2">
            <span className="material-symbols-outlined text-accent">auto_awesome</span>
            Assistentes
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {assistants.map((a) => (
              <AssistantCard key={a.id} assistant={a} onOpen={() => openAssistant(a)} />
            ))}
          </div>
        </section>
      )}

      <div className="flex gap-2 border-b border-surface-border mb-5">
        <TabButton active={tab === 'mine'} onClick={() => setTab('mine')} testid="tab-mine">
          Meus agentes ({mine.length})
        </TabButton>
        <TabButton active={tab === 'available'} onClick={() => setTab('available')} testid="tab-available">
          Disponíveis ({available.length})
        </TabButton>
      </div>

      {!loading && (
        <>
          {list.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="agents-grid">
              {list.map((agent) =>
                tab === 'mine' ? (
                  <AgentCard
                    key={agent.id}
                    agent={agent}
                    onOpen={() => navigate(`/agentes/${agent.id}`)}
                    onConfigure={() => navigate(`/agentes/${agent.id}/configurar`)}
                    onDelete={() => handleDelete(agent)}
                    pinned={pinnedIds.has(agent.id)}
                    onTogglePin={() => handleTogglePin(agent)}
                  />
                ) : (
                  <AgentCard
                    key={agent.id}
                    agent={agent}
                    onOpen={() => navigate(`/agentes/${agent.id}`)}
                    pinned={pinnedIds.has(agent.id)}
                    onTogglePin={() => handleTogglePin(agent)}
                  />
                )
              )}
            </div>
          ) : (
            <div
              data-testid="agents-empty"
              className="rounded-xl bg-surface-secondary border border-surface-border p-8 text-center text-content-secondary"
            >
              {tab === 'mine'
                ? 'Você ainda não criou nenhum agente. Clique em "Novo agente" pra começar.'
                : 'Nenhum agente compartilhado disponível pra você.'}
            </div>
          )}
        </>
      )}
    </div>
  );
};

const TabButton: FC<{ active: boolean; onClick: () => void; testid: string; children: React.ReactNode }> = ({
  active,
  onClick,
  testid,
  children,
}) => (
  <button
    type="button"
    data-testid={testid}
    onClick={onClick}
    className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition ${
      active
        ? 'border-accent text-accent'
        : 'border-transparent text-content-secondary hover:text-content-primary'
    }`}
  >
    {children}
  </button>
);

export default AgentsPage;
